import { useGetAssetTagsLazyQuery } from "graphqlBase/MappingAssetTag/__generated__/getAssetTags";
import { useGetMappingAssetTagsLazyQuery } from "graphqlBase/MappingAssetTag/__generated__/getMappingAssetTags";
import { useEffect, useMemo } from "react";
import { useRecoilState, useRecoilValue, useResetRecoilState, useSetRecoilState } from "recoil";
import {
  assetTagIdsToDeleteAtom,
  assetTagsforTagsListAtom,
  assetTagsToCreateAtom,
} from "../recoilState/assetTagsState";
import { selectedAssetsForTagsAtom } from "../recoilState/selectedAssetsForTagsState";
import { assetTagModalLoadingAtom, assignedAssetTagErrorAtom } from "../recoilState/tagModalState";
import useCreateAssetTags from "./useCreateAssetTags";
import useDeleteAssetTags from "./useDeleteAssetTags";

interface UseHandleUpdateTagsProps {
  afterMutation: () => Promise<void>;
  loading: boolean;
  onClose: () => void;
  open: boolean;
}

const useHandleUpdateAssetTags = ({
  afterMutation,
  loading,
  onClose,
  open,
}: UseHandleUpdateTagsProps & { onClose: () => void }) => {
  const [isLoading, setIsLoading] = useRecoilState(assetTagModalLoadingAtom);
  const setAssetTagsForTagsList = useSetRecoilState(assetTagsforTagsListAtom);
  const tagIdsToDelete = useRecoilValue(assetTagIdsToDeleteAtom);
  const resetCreateAssetTags = useResetRecoilState(assetTagsToCreateAtom);
  const resetTagIdsToDelete = useResetRecoilState(assetTagIdsToDeleteAtom);
  const resetAssignedTagError = useResetRecoilState(assignedAssetTagErrorAtom);
  const selectedAssets = useRecoilValue(selectedAssetsForTagsAtom);
  const selectedAssetIds = useMemo(
    () => Array.from(new Set((selectedAssets ?? []).map((asset) => asset.id).filter(Boolean))),
    [selectedAssets]
  );

  const { createAssetTagsLoading, handleCreateAssetTags } = useCreateAssetTags({
    selectedAssetIds,
    afterMutation,
  });
  const { deleteAssetTagsLoading, handleDeleteAssetTags } = useDeleteAssetTags({ selectedAssetIds, afterMutation });

  const [getAssetTagsQuery, { data: assetTagsData, loading: tagsLoading }] = useGetAssetTagsLazyQuery();

  const [
    getMappingAssetTagsQuery,
    { data: mappingAssetTagsData, loading: mappingAssetTagsLoading },
  ] = useGetMappingAssetTagsLazyQuery({ fetchPolicy: "network-only" });

  const handleCloseTagsModal = () => {
    onClose();
    resetTagIdsToDelete();
    resetCreateAssetTags();
    resetAssignedTagError();
    setAssetTagsForTagsList(undefined);
  };

  useEffect(() => {
    if (!open || !selectedAssetIds.length) {
      handleCloseTagsModal();
      return;
    }
    getAssetTagsQuery();
    getMappingAssetTagsQuery({ variables: { where: { assetId: { in: selectedAssetIds } } } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAssetTagsQuery, getMappingAssetTagsQuery, open, selectedAssetIds]);

  useEffect(() => {
    if (!mappingAssetTagsData?.mappingAssetTags?.items?.length) return;

    const mappingAssetTags = mappingAssetTagsData?.mappingAssetTags?.items ?? [];
    if (!mappingAssetTags?.length) {
      // setAssetTagsForTagsList([]);
      return;
    }

    const tagCountMap: { [key: string]: number } = {};
    mappingAssetTags.forEach((item) => {
      const tagId = item.assetTagId;
      tagCountMap[tagId] = (tagCountMap[tagId] || 0) + 1;
    });

    const assetTagsForTagList = mappingAssetTags
      .filter((item) => tagCountMap[item.assetTagId] === selectedAssetIds?.length)
      .map((item) => ({
        label: item.assetTag.tagValue,
        id: item.assetTag.id,
      }));

    const seenTagLabels = new Set<string>();
    const uniqueAssetTagsForTagList = assetTagsForTagList.filter((tag) => {
      if (seenTagLabels.has(tag.label)) return false;
      seenTagLabels.add(tag.label);
      return true;
    });
    setAssetTagsForTagsList(uniqueAssetTagsForTagList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mappingAssetTagsData?.mappingAssetTags?.items, selectedAssetIds]);

  const handleAddAndDelete = async () => {
    setIsLoading(true);

    await handleCreateAssetTags();
    if (tagIdsToDelete.length > 0) {
      const mappingAssetTags = mappingAssetTagsData?.mappingAssetTags?.items ?? [];
      await handleDeleteAssetTags({
        mappingAssetTags,
        ids: tagIdsToDelete,
      });
    }
    await afterMutation?.();
    handleCloseTagsModal();
    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  };

  const tagModalLoading =
    (createAssetTagsLoading || deleteAssetTagsLoading || tagsLoading || mappingAssetTagsLoading || loading) ??
    isLoading ??
    false;
  return { handleAddAndDelete, tagModalLoading, assetTagsData, handleCloseTagsModal };
};

export default useHandleUpdateAssetTags;
